import React, { FC, useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import Header from "components/header/Header.container";
import Footer from "components/footer/Footer";
import Loader from "components/loader/loader";
import newsPlaceholder from "assets/svg/news-placeholder-image.svg";
import { NewsItem, NewsDetail } from "views/news/newsTypes";
import { fetchNewsById, fetchRecentNews } from "views/news/fetchNews";
import { useNewsRealtime } from "views/news/useNewsRealtime";
import useExternalLinksNewTab from "utils/useExternalLinksNewTab";
import { BASE_URL } from "store/services/URL";
import Styles from "./NewsDetailPage.module.scss";
import SEOMeta from "components/common/SEOMeta";
import { htmlToMetaDescription } from "utils/seoText";
import { getSiteBaseUrl } from "utils/seoCanonical";

const absolutizeMediaUrls = (html: string): string => {
  const origin = BASE_URL.replace(/\/+$/, "");
  return html.replace(
    /(src\s*=\s*["'])(\/(?:media|summernote|ckeditor5)\/)/gi,
    `$1${origin}$2`
  );
};

const NewsDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [detail, setDetail] = useState<NewsDetail | null>(null);
  const [recent, setRecent] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  // Open external source links (embedded in the content) in a new tab. Keyed on
  // the *rendered* content (empty while loading) so the effect re-runs once the
  // content div is actually mounted — not on an intermediate unbatched render.
  useExternalLinksNewTab(contentRef, loading ? "" : detail?.content);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    window.scrollTo(0, 0);

    fetchNewsById(Number(id)).then((result) => {
      if (!cancelled) {
        setDetail(result);
        setLoading(false);
      }
    });

    fetchRecentNews(4, id).then((items) => {
      if (!cancelled) setRecent(items);
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Real-time: refresh the "Recent News" sidebar when news is published (no toast).
  useNewsRealtime(() => {
    fetchRecentNews(4, id).then((items) => setRecent(items));
  });

  const goToNews = (newsId: number) => history.push(`/news/${newsId}`);

  const newsTitle = detail?.title || detail?.description;
  const newsDescription = detail
    ? htmlToMetaDescription(detail.content || detail.description || "") ||
      "News on Moderate Politics."
    : undefined;
  const newsImageSource = detail?.heroImage || detail?.image;
  const newsImage = newsImageSource
    ? newsImageSource.startsWith("http")
      ? newsImageSource
      : `${getSiteBaseUrl()}${newsImageSource}`
    : undefined;

  return (
    <div className="pageShell">
      <SEOMeta
        title={newsTitle}
        description={newsDescription}
        ogType="article"
        ogImage={newsImage}
        noIndex
      />
      <Header />
      <div className="pageShellMain">
        <div className="custom-container">
          <div className={Styles.page}>
            {loading && (
              <div className={Styles.stateBlock}>
                <Loader />
              </div>
            )}

            {!loading && !detail && (
              <p className={Styles.stateBlock}>Article not found.</p>
            )}

            {!loading && detail && (
              <>
                <div className={Styles.hero}>
                  <img
                    className={Styles.heroImg}
                    src={detail.heroImage || detail.image || newsPlaceholder}
                    alt=""
                  />
                </div>

                <div className={Styles.layout}>
                  <div className={Styles.main}>
                    <header className={Styles.titleBlock}>
                      <h1 className={Styles.title}>
                        {detail.title || detail.description}
                      </h1>
                      {detail.label && (
                        <p className={Styles.sourceLabel}>{detail.label}</p>
                      )}
                    </header>

                    <div
                      ref={contentRef}
                      className={Styles.body}
                      dangerouslySetInnerHTML={{
                        __html: absolutizeMediaUrls(detail.content || ""),
                      }}
                    />
                  </div>

                  <aside className={Styles.sidebar}>
                    <h3 className={Styles.sidebarHeading}>Recent News</h3>
                    {recent.length === 0 && (
                      <p className={Styles.recentEmpty}>No recent news.</p>
                    )}
                    {recent.map((item) => (
                      <div
                        key={item.id}
                        className={Styles.recentCard}
                        role="button"
                        tabIndex={0}
                        onClick={() => goToNews(item.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            goToNews(item.id);
                          }
                        }}
                        aria-label={`Read: ${item.description}`}
                      >
                        <img
                          className={Styles.recentThumb}
                          src={item.image || newsPlaceholder}
                          alt=""
                        />
                        <div className={Styles.recentBody}>
                          <p className={Styles.recentTitle}>
                            {item.description}
                          </p>
                          <span className={Styles.recentReadMore}>
                            Read More
                          </span>
                        </div>
                      </div>
                    ))}
                  </aside>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NewsDetailPage;
